<template>
  <div class="activity-catalog">
    <div class="catalog-card">
      <div class="catalog-header">
        <div class="header-icon">
          <GridIcon width="48" height="48" />
        </div>
        <h1 class="catalog-title">Makercart Activity Catalog</h1>
        <p class="catalog-description">
          Browse our catalog of 100+ Makercart activities. Each includes lesson
          materials you can preview before deciding on your purchase.
        </p>
      </div>

      <div class="activities-grid">
        <template v-if="isLoading">
          <div v-for="n in 6" :key="`skeleton-${n}`" class="activity-skeleton">
            <div class="skeleton-image"></div>
            <div class="skeleton-content">
              <div class="skeleton-title"></div>
              <div class="skeleton-line"></div>
              <div class="skeleton-line short"></div>
            </div>
          </div>
        </template>
        <ItemMakerCart
          v-else
          v-for="activity in activities"
          :key="activity.makercart_id"
          :item="activity"
          :is-logged="true"
          @item-clicked="handleItemClick(activity)"
          @update-liked-status="handleLikeUpdate"
          :can-like="false"
        />
      </div>

      <div class="whats-included">
        <h2 class="included-title">What's Included</h2>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon" style="background-color: #e6f4ef">
              <BookIcon color="#00925E" />
            </div>
            <h3 class="feature-title">100+ Activities</h3>
            <p class="feature-description">
              Comprehensive collection of hands-on maker activities
            </p>
          </div>
          <div class="feature-item">
            <div class="feature-icon" style="background-color: #ededfc">
              <ClockIcon width="24" height="24" color="#4c4ddc" />
            </div>
            <h3 class="feature-title">Ready-to-Use</h3>
            <p class="feature-description">
              Complete lesson materials included
            </p>
          </div>
          <div class="feature-item">
            <div class="feature-icon" style="background-color: #e6f4ef">
              <StarIcon width="24" height="24" />
            </div>
            <h3 class="feature-title">Preview First</h3>
            <p class="feature-description">
              Review all materials before purchasing
            </p>
          </div>
        </div>
      </div>

      <div class="button-container">
        <ActionButton
          @click="handleBack"
          variant="outline"
          class="back-button"
          :style="{ marginBottom: '0' }"
        >
          Back</ActionButton
        >
        <ActionButton
          @click="handleStartBrowsing"
          variant="primary"
          class="start-button"
          >Start Browsing
        </ActionButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, onMounted } from "vue";
import GridIcon from "@/components/icons/GridIcon.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import ItemMakerCart from "@/components/maker-cart/ItemMakerCart.vue";
import BookIcon from "@/components/icons/BookIcon.vue";
import ClockIcon from "@/components/icons/ClockIcon.vue";
import { StarIcon } from "@/components/icons";
import stemStudioService from "@/services/stemStudioService";
import { useLocaleStore } from "@/store/locale";
import { useRouter } from "vue-router";

const emit = defineEmits(["back", "start-browsing"]);

const router = useRouter();

const localeStore = useLocaleStore();
const activities = ref([]);
const isLoading = ref(false);

const loadActivities = async () => {
  try {
    isLoading.value = true;
    const params = {
      locale: localeStore.currentLocale || "US",
      limit: 6,
      popular: true,
    };
    const response = await stemStudioService.getAll(params);

    if (response?.data?.data?.rows) {
      activities.value = response.data.data.rows.map((item) => ({
        ...item,
        image:
          item.image_thumbnail && item.image_thumbnail.length > 0
            ? item.image_thumbnail[0]
            : "/images/placeholder-activity.jpg",
        liked: false,
      }));
    }
  } catch (error) {
    console.error("Error loading activities:", error);
    activities.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadActivities();
});

const handleBack = () => {
  emit("back");
};

const handleStartBrowsing = () => {
  router.push({ name: "MakerCartPage" });
  emit("start-browsing");
};

const handleItemClick = (activity) => {
  console.log("Activity clicked:", activity);
};

const handleLikeUpdate = (data) => {
  const activity = activities.value.find((a) => a.makercart_id === data.id);
  if (activity) {
    activity.liked = data.liked;
  }
};
</script>

<style scoped>
.activity-catalog {
  background-color: #f2f3fb;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  min-height: 100vh;
  width: 100%;
}

.catalog-card {
  background-color: white;
  border: 1px solid #c0c0fd;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  padding: 0 0 32px 0;
  border-radius: 16px;
  width: 100%;
  max-width: 1200px;
}

.catalog-header {
  background-color: var(--green-dark-1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 32px;
  width: 100%;
  border-radius: 16px 16px 0 0;
}

.header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.catalog-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 45px;
  line-height: 52px;
  color: white;
  text-align: center;
  margin: 0;
}

.catalog-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  text-align: center;
  margin: 0;
  letter-spacing: 0.08px;
}

.activities-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  padding: 0 32px;
}

.activity-skeleton {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-image {
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-title {
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  width: 80%;
}

.skeleton-line {
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  width: 100%;
}

.skeleton-line.short {
  width: 60%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.whats-included {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 100%;
  padding: 0 32px;
}

.included-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: #3b4648;
  text-align: center;
  margin: 0;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
}

.feature-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  text-align: center;
}

.feature-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
}

.feature-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--neutral-700);
  margin: 0;
}

.button-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 32px;
}

.back-button {
  background-color: white;
  border: 1px solid #bac1cc;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  min-width: 126px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
  color: #3b4648;
}

.start-button {
  background-color: var(--green-dark-1);
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.start-button:hover {
  background-color: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

@media (max-width: 768px) {
  .activities-grid {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 16px;
  }

  .features-grid {
    gap: 16px;
  }

  :deep(.item-maker-cart) {
    height: 185px;
  }

  :deep(.image-container) {
    height: 100%;
  }

  :deep(.title-section) {
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  :deep(.item-info-container) {
    transform: translateY(calc(100% - 65px)) !important;
  }

  .feature-icon {
    width: 48px;
    height: 48px;
  }

  .feature-title {
    font-size: var(--fs-big);
    font-style: normal;
    font-weight: 400;
  }

  .catalog-title {
    font-size: 32px;
    line-height: 40px;
  }

  .back-button,
  .start-button {
    width: 100%;
  }
}
</style>
